import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiFolder, FiDownload, FiTrash2, FiDollarSign } from 'react-icons/fi';
import './FinanceProjects.css';

const formatMoney = (v, c) => {
  try {
    return `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${c || ''}`.trim();
  } catch {
    return `${v} ${c || ''}`;
  }
};

const FinanceProjects = ({ token, user }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ name: '', client: '', description: '', baseCurrency: 'USD', language: 'en', profitPercent: 0, profitVisible: true });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setProjects(json.financeProjects || []);
    } catch (e) {
      console.error('Failed to load finance projects', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoards = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      setBoards(json.boards || []);
    } catch (e) {
      console.error('Failed to load boards', e);
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchBoards();
    // eslint-disable-next-line
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          profitPercent: Number(form.profitPercent) || 0,
          baseCurrency: (form.baseCurrency || 'USD').toUpperCase(),
        }),
      });
      const json = await res.json();
      if (json.financeProject) {
        setCreateOpen(false);
        setForm({ name: '', client: '', description: '', baseCurrency: 'USD', language: 'en', profitPercent: 0, profitVisible: true });
        fetchProjects();
        navigate(`/finance-projects/${json.financeProject.id}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleImport = async (boardId) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/import-board/${boardId}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (json.financeProject) {
        setImportOpen(false);
        fetchProjects();
        navigate(`/finance-projects/${json.financeProject.id}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete finance project "${name}"? This will remove all its items and payments.`)) return;
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/finance-projects/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchProjects();
  };

  const importedBoardIds = new Set(projects.filter((p) => p.sourceBoardId).map((p) => p.sourceBoardId));
  const importableBoards = boards.filter((b) => !importedBoardIds.has(b.id));

  return (
    <div className="finance-projects-page" data-testid="finance-projects-page">
      <div className="finance-projects-header">
        <div>
          <div className="finance-projects-title-row">
            <FiDollarSign size={22} />
            <h1 className="finance-projects-title">Quotations & Costing</h1>
          </div>
          <div className="finance-projects-subtitle">
            Internal cost vs client price, with payments and quotations.
          </div>
        </div>
        <div className="finance-projects-actions">
          <button className="btn btn-secondary" onClick={() => setImportOpen(true)} data-testid="import-board-btn">
            <FiFolder size={14} /> Import from Board
          </button>
          <button className="btn btn-primary" onClick={() => setCreateOpen(true)} data-testid="create-finance-project-btn">
            <FiPlus size={14} /> New Project
          </button>
        </div>
      </div>

      {loading ? (
        <div className="finance-projects-empty">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="finance-projects-empty" data-testid="finance-projects-empty">
          <h3>No finance projects yet</h3>
          <p>Create a manual project or import an existing board to start tracking costs vs client prices.</p>
        </div>
      ) : (
        <div className="finance-projects-grid">
          {projects.map((p) => (
            <div
              key={p.id}
              className="fp-card"
              onClick={() => navigate(`/finance-projects/${p.id}`)}
              data-testid="finance-project-card"
            >
              <div className="fp-card-head">
                <h3 className="fp-card-title">{p.name}</h3>
                {p.sourceBoardId && (
                  <span className="fp-card-pill" title="Synced from a Board">Synced</span>
                )}
              </div>
              {p.client && <div className="fp-card-client">{p.client}</div>}
              {/* Phase 1 badges (Part 7) */}
              <div className="fp-card-badges" data-testid="fp-card-badges">
                <span
                  className={`fp-badge ${p.linkedBoardId ? 'fp-badge-linked' : 'fp-badge-standalone'}`}
                  title={p.linkedBoardId ? 'Linked to a board' : 'Standalone (no board)'}
                  data-testid="fp-badge-link"
                >
                  {p.linkedBoardId ? 'Linked' : 'Standalone'}
                </span>
                <span
                  className={`fp-badge ${p.visibility === 'client_visible' ? 'fp-badge-client' : 'fp-badge-internal'}`}
                  title={p.visibility === 'client_visible' ? 'Visible to client' : 'Internal only'}
                  data-testid="fp-badge-visibility"
                >
                  {p.visibility === 'client_visible' ? 'Client visible' : 'Internal only'}
                </span>
                <span className="fp-badge fp-badge-purpose" title="Project purpose" data-testid="fp-badge-purpose">
                  {(() => {
                    const labels = {
                      quotation: 'Quotation',
                      internal_costing: 'Internal Costing',
                      execution_budget: 'Execution Budget',
                      variation_order: 'Variation Order',
                    };
                    return labels[p.purpose] || 'Quotation';
                  })()}
                </span>
                <span
                  className={`fp-badge fp-badge-sync fp-badge-sync-${p.syncMode || 'none'}`}
                  title={`Sync mode: ${p.syncMode || 'none'}`}
                  data-testid="fp-badge-sync"
                >
                  {{ none: 'Sync: None', manual: 'Sync: Manual', live: 'Sync: Live' }[p.syncMode] || 'Sync: None'}
                </span>
              </div>
              <div className="fp-card-stats">
                <div className="fp-stat">
                  <div className="fp-stat-label">Tasks</div>
                  <div className="fp-stat-value">{p.itemsCount || 0}</div>
                </div>
                <div className="fp-stat">
                  <div className="fp-stat-label">Cost</div>
                  <div className="fp-stat-value">{formatMoney(p.costBase, p.baseCurrency)}</div>
                </div>
                <div className="fp-stat">
                  <div className="fp-stat-label">Sell</div>
                  <div className="fp-stat-value">{formatMoney(p.sellBase, p.baseCurrency)}</div>
                </div>
                <div className="fp-stat">
                  <div className="fp-stat-label">Total Profit</div>
                  <div className={`fp-stat-value ${p.marginBase >= 0 ? 'pos' : 'neg'}`}>
                    {formatMoney(p.marginBase, p.baseCurrency)}
                  </div>
                </div>
              </div>
              <div className="fp-card-footer">
                <span className="fp-card-currency">Base: {p.baseCurrency}</span>
                <button
                  className="fp-card-delete"
                  onClick={(e) => { e.stopPropagation(); handleDelete(p.id, p.name); }}
                  title="Delete project"
                  data-testid="delete-finance-project-btn"
                >
                  <FiTrash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {createOpen && (
        <div className="modal-overlay" onClick={() => setCreateOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="create-finance-project-modal">
            <h2>New Finance Project</h2>
            <form onSubmit={handleCreate}>
              <div className="form-row">
                <label>Name *</label>
                <input className="input" autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="fp-form-name" />
              </div>
              <div className="form-row">
                <label>Client</label>
                <input className="input" value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} data-testid="fp-form-client" />
              </div>
              <div className="form-row">
                <label>Description</label>
                <textarea className="input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-grid-2">
                <div className="form-row">
                  <label>Base Currency</label>
                  <input className="input" value={form.baseCurrency} maxLength={4} onChange={(e) => setForm({ ...form, baseCurrency: e.target.value.toUpperCase() })} data-testid="fp-form-currency" />
                </div>
                <div className="form-row">
                  <label>Profit % (on Cost)</label>
                  <input className="input" type="number" step="0.01" value={form.profitPercent} onChange={(e) => setForm({ ...form, profitPercent: e.target.value })} data-testid="fp-form-profit" />
                </div>
              </div>
              <div className="form-row">
                <label>Quotation / Client Portal Language</label>
                <select
                  className="input"
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  data-testid="fp-form-language"
                >
                  <option value="en">English</option>
                  <option value="ar">العربية (Arabic)</option>
                  <option value="tr">Türkçe (Turkish)</option>
                </select>
              </div>
              <div className="form-row form-row-checkbox">
                <label>
                  <input type="checkbox" checked={form.profitVisible} onChange={(e) => setForm({ ...form, profitVisible: e.target.checked })} />
                  <span>Show profit as a separate line on the quotation (otherwise distributed into each task price)</span>
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="fp-form-submit">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="modal-overlay" onClick={() => setImportOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="import-board-modal">
            <h2>Import from a Board</h2>
            <p className="form-helper">
              Pick an existing board. Its tasks will be mirrored into a new Finance Project.
              Future task additions/deletions on that board will auto-sync here.
            </p>
            {importableBoards.length === 0 ? (
              <div className="empty-state">All boards have already been imported.</div>
            ) : (
              <div className="import-board-list">
                {importableBoards.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    className="import-board-item"
                    onClick={() => handleImport(b.id)}
                    data-testid="import-board-item"
                  >
                    <span className="import-board-color" style={{ backgroundColor: b.color || '#0F0F10' }} />
                    <div className="import-board-info">
                      <div className="import-board-name">{b.name}</div>
                      {b.client && <div className="import-board-client">{b.client}</div>}
                    </div>
                    <FiDownload size={16} />
                  </button>
                ))}
              </div>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setImportOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceProjects;
